/**
 * @file Алгоритм скачивания с ограниченным окном параллельных запросов.
 *
 * Ответы могут прийти в разном порядке, но записываются строго по диапазонам.
 * ports отделяет алгоритм от сети и диска; events сообщает состояние в Provider.
 */
import { withAbort } from '../../services/abort.ts'
import { DOWNLOAD_CHUNK_SIZE, DOWNLOAD_WINDOW, DownloadError } from './model.ts'
import type {
    DownloadMetadata,
    DownloadPhase,
    DownloadRange,
    DownloadRequest,
    DownloadSource,
    DownloadWriter,
} from './model.ts'

export interface DownloadPorts {
    open: (
        source: DownloadSource,
        metadata: DownloadMetadata,
        signal: AbortSignal,
    ) => Promise<DownloadWriter>
    prepare: (
        request: DownloadRequest,
        signal: AbortSignal,
    ) => Promise<DownloadSource>
    metadata: (
        request: DownloadRequest,
        source: DownloadSource,
        signal: AbortSignal,
    ) => Promise<DownloadMetadata>
    quota: (
        request: DownloadRequest,
        metadata: DownloadMetadata,
        signal: AbortSignal,
    ) => Promise<void>
    fetch: (
        request: DownloadRequest,
        source: DownloadSource,
        range: DownloadRange,
        signal: AbortSignal,
    ) => Promise<Uint8Array<ArrayBuffer>>
    verify: (
        request: DownloadRequest,
        source: DownloadSource,
        metadata: DownloadMetadata,
        signal: AbortSignal,
    ) => Promise<void>
    cleanup: (request: DownloadRequest, source: DownloadSource) => Promise<void>
}

export interface DownloadEvents {
    phase: (phase: DownloadPhase) => void
    metadata: (source: DownloadSource, metadata: DownloadMetadata) => void
    progress: (written: number) => void
    cleanupWarning: () => void
}

export async function runDownload(
    request: DownloadRequest,
    ports: DownloadPorts,
    signal: AbortSignal,
    events: DownloadEvents,
): Promise<void> {
    const controller = new AbortController()

    // Пользователь может отменить задачу; ошибка одного диапазона отменяет остальные.
    const combined = AbortSignal.any([signal, controller.signal])

    // Ресурсы для завершения и очистки существуют до конца всей операции.
    let writer: DownloadWriter | null = null
    let source: DownloadSource | null = null

    // Очередь диапазонов хранит обещанные результаты, а не весь файл целиком.
    const pending = new Map<
        number,
        Promise<{ data: Uint8Array<ArrayBuffer> } | { error: unknown }>
    >()

    // Первая ошибка и факт запроса архива нужны даже после отмены дочерних операций.
    let failure: unknown = null
    let archiveRequested = false

    // Единая обёртка операций диска: сетевые ошибки и отказ записи показываются
    // пользователю по-разному. Если отменена вся задача, сохраняем причину отмены.
    async function saving<T>(
        operation: () => Promise<T>,
        operationSignal: AbortSignal,
    ): Promise<T> {
        try {
            return await withAbort(operation(), operationSignal)
        } catch (error) {
            combined.throwIfAborted()
            // Тайм-аут браузерного потока не доказывает нехватку места на диске.
            if (operationSignal.aborted) throw new DownloadError('saveTimeout')
            if (error instanceof DownloadError) throw error

            throw new DownloadError('write')
        }
    }

    try {
        combined.throwIfAborted()
        if (!request.name || /[/\\\0]/.test(request.name))
            throw new DownloadError('protocol')

        // Для файла prepare возвращает исходный путь, для папки ждёт готовый архив.
        events.phase(request.kind === 'folder' ? 'preparing' : 'checking')
        archiveRequested = request.kind === 'folder'
        source = await withAbort(ports.prepare(request, combined), combined)
        combined.throwIfAborted()

        // Метаданные нужны до открытия записываемого файла: определяем размер
        // и проверяем остаток квоты из уже полученных данных.
        events.phase('checking')
        const metadata = await withAbort(
            ports.metadata(request, source, combined),
            combined,
        )
        if (!Number.isSafeInteger(metadata.size) || metadata.size < 0)
            throw new DownloadError('protocol')

        events.metadata(source, metadata)
        await withAbort(ports.quota(request, metadata, combined), combined)
        combined.throwIfAborted()

        // Открываем браузерный поток только после проверки файла и квоты.
        const opening = AbortSignal.any([combined, AbortSignal.timeout(30_000)])
        writer = await saving(
            () => ports.open(source!, metadata, opening),
            opening,
        )
        events.phase('downloading')

        // scheduled — сколько диапазонов запущено, next — какой надо записать,
        // written — сколько байтов уже принял адаптер записи. Они могут различаться.
        let scheduled = 0
        let next = 0
        let written = 0
        const currentSource = source

        // Окно включает и запросы в работе, и готовые, но ещё не записанные ответы.
        // Поэтому медленный первый диапазон не приводит к накоплению всего файла в памяти.
        function fill() {
            while (
                pending.size < DOWNLOAD_WINDOW &&
                scheduled * DOWNLOAD_CHUNK_SIZE < metadata.size
            ) {
                const index = scheduled++
                const range = {
                    start: index * DOWNLOAD_CHUNK_SIZE,
                    end: Math.min(
                        (index + 1) * DOWNLOAD_CHUNK_SIZE,
                        metadata.size,
                    ),
                }

                const promise = withAbort(
                    ports.fetch(request, currentSource, range, combined),
                    combined,
                ).then(
                    (data) => ({ data }),
                    (error) => {
                        // Сохраняем первую причину. После abort соседние запросы
                        // тоже завершатся ошибкой, но не должны её затереть.
                        if (failure === null) {
                            failure = error
                            controller.abort(error)
                        }

                        // Ошибка становится значением Promise. Так поздний ответ
                        // не создаст unhandled rejection, пока очередь ждёт другой диапазон.
                        return { error }
                    },
                )
                pending.set(index, promise)
            }
        }

        fill()

        // Ждём именно следующий диапазон, даже если последующие уже скачались.
        while (pending.size > 0) {
            const result = await withAbort(pending.get(next)!, combined)
            if ('error' in result) throw result.error

            const expected = Math.min(
                DOWNLOAD_CHUNK_SIZE,
                metadata.size - written,
            )
            if (result.data.byteLength !== expected)
                throw new DownloadError('incomplete')

            await saving(
                () => writer!.write(result.data),
                AbortSignal.any([combined, AbortSignal.timeout(30_000)]),
            )
            combined.throwIfAborted()
            // Прогресс растёт только после принятия данных адаптером записи.
            written += result.data.byteLength
            events.progress(written)

            // Освободили одно место в окне — можно начать следующий диапазон.
            // Без этой связи с записью готовые ответы накапливались бы в памяти.
            pending.delete(next++)
            fill()
        }

        // Повторный HEAD перед close обнаруживает изменение размера исходного файла.
        if (written !== metadata.size) throw new DownloadError('incomplete')

        await withAbort(
            ports.verify(request, source, metadata, combined),
            combined,
        )
        combined.throwIfAborted()
        events.phase('saving')
        await saving(
            () => writer!.close(),
            AbortSignal.any([combined, AbortSignal.timeout(30_000)]),
        )
    } catch (error) {
        // Сначала останавливаем оставшиеся чтения, затем прерываем запись.
        // failure содержит исходный сбой, даже если ожидание уже получило abort.
        controller.abort(error)
        if (writer) {
            await withAbort(
                writer.abort(error),
                AbortSignal.timeout(5_000),
            ).catch(() => {})
        }

        throw failure ?? error
    } finally {
        // finally выполняется и при успехе, и при ошибке. Временный архив
        // очищается отдельной попыткой; её сбой не заменяет результат скачивания.
        controller.abort()
        await Promise.allSettled(pending.values())

        // Отмена подготовки не отменяет серверную задачу: архив мог продолжить создаваться.
        if (archiveRequested && !source) events.cleanupWarning()

        if (source?.temporary) {
            await withAbort(
                ports.cleanup(request, source),
                AbortSignal.timeout(10_000),
            ).catch(() => events.cleanupWarning())
        }
    }
}
